import { csvSafeText } from "./csv-safety";
/**
 * Export file builders shared by the browser and the API: a store-only zip (no compression, so no
 * library) and the CSV French Excel expects when a file is opened by double-click.
 */

const CRC_TABLE = Array.from({ length: 256 }, (_, n) => {
  let c = n;
  for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  return c >>> 0;
});

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) crc = CRC_TABLE[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

export interface ZipFile {
  name: string;
  data: Uint8Array;
  /** Shown as the file's date by archive tools; left empty (1980) when absent. */
  modifiedAt?: Date;
  /** Unix permissions (0o755 for a program): kept by macOS and Linux when the zip is opened. */
  mode?: number;
}

/** MS-DOS date and time, as zip headers store them (local wall-clock time, 2-second steps). */
function dosDateTime(date: Date | undefined): { date: number; time: number } {
  if (!date || date.getFullYear() < 1980) return { date: 0x21, time: 0 };
  return {
    date: ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2),
  };
}

/** Uncompressed (method 0) zip: exports hold already-compressed PDFs or small XML parts. */
export function zipStore(files: ZipFile[]): Uint8Array<ArrayBuffer> {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const file of files) {
    const name = encoder.encode(file.name);
    const crc = crc32(file.data);
    const stamp = dosDateTime(file.modifiedAt);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, 0x0800, true);
    local.setUint16(10, stamp.time, true);
    local.setUint16(12, stamp.date, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, file.data.length, true);
    local.setUint32(22, file.data.length, true);
    local.setUint16(26, name.length, true);
    chunks.push(new Uint8Array(local.buffer), name, file.data);
    const entry = new DataView(new ArrayBuffer(46));
    entry.setUint32(0, 0x02014b50, true);
    // « Made by » Unix (3) when permissions are given, so unzip tools apply them.
    entry.setUint16(4, file.mode === undefined ? 20 : (3 << 8) | 20, true);
    entry.setUint16(6, 20, true);
    entry.setUint16(8, 0x0800, true);
    entry.setUint16(12, stamp.time, true);
    entry.setUint16(14, stamp.date, true);
    entry.setUint32(16, crc, true);
    entry.setUint32(20, file.data.length, true);
    entry.setUint32(24, file.data.length, true);
    entry.setUint16(28, name.length, true);
    if (file.mode !== undefined) entry.setUint32(38, ((0o100000 | file.mode) << 16) >>> 0, true);
    entry.setUint32(42, offset, true);
    central.push(new Uint8Array(entry.buffer), name);
    offset += 30 + name.length + file.data.length;
  }
  const centralSize = central.reduce((sum, c) => sum + c.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);
  const parts = [...chunks, ...central, new Uint8Array(end.buffer)];
  const out = new Uint8Array(parts.reduce((sum, p) => sum + p.length, 0));
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

export type CsvCell = string | number | null | undefined;

function escapeCsv(value: CsvCell) {
  const text = value === null || value === undefined ? "" : typeof value === "number" ? String(value) : csvSafeText(value);
  return /[";\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

/** Semicolon separator and UTF-8 BOM: what French Excel expects when a file is opened by double-click. */
export function csvText(header: string[], rows: CsvCell[][]): string {
  return `\uFEFF${[header, ...rows].map((row) => row.map(escapeCsv).join(";")).join("\r\n")}`;
}

/** Decimal amount for spreadsheets (no currency symbol, locale decimal separator). */
export function csvAmount(cents: number, locale: string) {
  return (cents / 100).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
}
